import * as yup from '@if/core/yup'
import {
  ActionMenuItem,
  ControlledChipsField,
  ControlledSelect,
  type FormDialogContentContext,
  HelperText,
  MetaValue,
  useFormDialog,
} from '@if/mui'
import { Stack } from '@mui/material'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useUpdateMerchantReportSettings } from '@src/data/api/merchants-api/merchants-api'
import { EReportGenerationFrequency } from '@src/types/Merchant'
import { FileBarChart, Save } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const reportSettingsSchema = yup.object({
  reportGenerationFrequency: yup
    .mixed<EReportGenerationFrequency>()
    .oneOf([EReportGenerationFrequency.Daily, EReportGenerationFrequency.Weekly, EReportGenerationFrequency.Monthly])
    .nullable()
    .default(null),
  reportRecipientsEmailList: yup.array().of(yup.text().email().required()).default([]),
})

type ReportSettingsFormData = yup.InferSubmit<typeof reportSettingsSchema>

type ReportSettingsCtx = FormDialogContentContext<ReportSettingsFormData, void, void>

const ReportSettingsDialogContent = ({ ctx, merchantName }: { ctx: ReportSettingsCtx; merchantName: string }) => {
  const { t } = useTranslation()
  const { control, trigger } = ctx.form

  const frequencyOptions = useMemo(
    () =>
      Object.values(EReportGenerationFrequency)
        .filter((f) => f !== EReportGenerationFrequency.None)
        .map((f) => (
          <ActionMenuItem key={f} value={f}>
            {t(`reportSettings.frequency.${f}`)}
          </ActionMenuItem>
        )),
    [t],
  )

  return (
    <Stack spacing={3} sx={{ pt: 1 }}>
      <ControlledSelect
        control={control}
        name="reportGenerationFrequency"
        label={t('reportSettings.frequency.label')}
        emptyOptionLabel={t('common.never')}
        options={frequencyOptions}
      />
      <ControlledChipsField
        control={control}
        trigger={trigger}
        name="reportRecipientsEmailList"
        label={t('reportSettings.recipients.label')}
        helperText={t('reportSettings.recipients.helper')}
      />
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <HelperText>{t('reportSettings.merchantContext')}</HelperText>
        <MetaValue>{merchantName}</MetaValue>
      </Stack>
    </Stack>
  )
}

/**
 * The Report Settings dialog, wired through the shared `useFormDialog` API — the
 * dialog-based replacement for the old routed ReportSettingsPage. Opened as an
 * `edit` form (Confirm enables on dirty + valid), seeded from the merchant's
 * current delivery frequency + recipient list, and persisted through
 * `useUpdateMerchantReportSettings`. Opened imperatively from the sidebar
 * "Report settings" action in the DashboardShell parameters section.
 */
export const useReportSettingsDialog = () => {
  const { t } = useTranslation()
  const merchant = useMerchant()
  const updateSettings = useUpdateMerchantReportSettings()

  const currentFrequency =
    merchant.reportGenerationFrequency && merchant.reportGenerationFrequency !== EReportGenerationFrequency.None
      ? merchant.reportGenerationFrequency
      : null

  const dialog = useFormDialog<ReportSettingsFormData, void, void>({
    icon: FileBarChart,
    title: t('reportSettings.title'),
    subtitle: t('reportSettings.subtitle'),
    confirmIcon: Save,
    maxWidth: 'sm',
    fullWidth: true,
    schema: reportSettingsSchema,
    defaultValues: {
      reportGenerationFrequency: currentFrequency,
      reportRecipientsEmailList: merchant.reportRecipientsEmailList ?? [],
    },
    content: (ctx) => <ReportSettingsDialogContent ctx={ctx} merchantName={merchant.name} />,
    successMessage: t('reportSettings.savedSuccess'),
    onSubmit: async (data) => {
      await updateSettings.mutateAsync({
        merchantId: merchant.id,
        dto: {
          reportGenerationFrequency: data.reportGenerationFrequency ?? EReportGenerationFrequency.None,
          reportRecipientsEmailList: data.reportRecipientsEmailList ?? [],
        },
      })
    },
  })

  return { open: () => dialog.edit() }
}
